// Multi-category aware counterpart to extractionFields.js. The generic
// editor in AdminWorkflowConfigPage.vue only ever reads/writes
// document_extraction[0], which silently drops edits for every other
// category in a multi-category workflow like form3_baumschutz's
// DOCUMENT_CATEGORISE config. These helpers address a category by its
// document_type instead of by array position.

function findDocumentProcessorStep(configuration) {
  const steps = configuration?.steps;
  if (!Array.isArray(steps)) return null;
  return steps.find((s) => s?.type === 'document_processor' && Array.isArray(s?.config?.document_extraction)) || null;
}

function findExtraction(configuration, documentType) {
  const step = findDocumentProcessorStep(configuration);
  if (!step) return null;
  return step.config.document_extraction.find((e) => e.document_type === documentType) || null;
}

export function getCategories(configuration) {
  const step = findDocumentProcessorStep(configuration);
  if (!step) return [];
  return step.config.document_extraction.map((e) => e.document_type);
}

export function getFieldsForCategory(configuration, documentType) {
  const extraction = findExtraction(configuration, documentType);
  const modelOutput = extraction?.model_output;
  if (!modelOutput) return [];
  const requiredFields = modelOutput.required_fields || [];
  const descriptions = modelOutput.descriptions || {};
  return requiredFields.map((name) => ({ name, description: descriptions[name] || '' }));
}

export function setFieldsForCategory(configuration, documentType, fields) {
  const extraction = findExtraction(configuration, documentType);
  if (!extraction) return configuration;
  extraction.model_output = extraction.model_output || { name: 'FormIQExtractor' };

  let previousInstructions = {};
  try {
    previousInstructions = JSON.parse(extraction.model_output.format_instructions || '{}');
  } catch {
    previousInstructions = {};
  }

  extraction.model_output.required_fields = fields.map((f) => f.name);
  extraction.model_output.descriptions = Object.fromEntries(fields.map((f) => [f.name, f.description]));
  extraction.model_output.format_instructions = JSON.stringify(
    Object.fromEntries(fields.map((f) => [f.name, Object.prototype.hasOwnProperty.call(previousInstructions, f.name) ? previousInstructions[f.name] : '']))
  );
  return configuration;
}

function defaultCategoryTemplate(documentType) {
  return {
    document_type: documentType,
    use_ocr: false,
    model_output: {
      name: 'FormIQExtractor',
      format_instructions: '{"predicted_document_type": "", "predicted_document_type_certainity": ""}',
      required_fields: ['predicted_document_type', 'predicted_document_type_certainity'],
      descriptions: {
        predicted_document_type: `The single most likely document type. Must be exactly one of: ${documentType}.`,
        predicted_document_type_certainity: 'Certainty of the predicted type. Can be HIGH, MEDIUM, LOW',
      },
    },
  };
}

export function addCategory(workflow, documentType) {
  const step = findDocumentProcessorStep(workflow.configuration);
  if (!step) return workflow;
  if (step.config.document_extraction.some((e) => e.document_type === documentType)) return workflow;

  step.config.document_extraction.push(defaultCategoryTemplate(documentType));
  workflow.meta_data = workflow.meta_data || {};
  workflow.meta_data.document_types = [...(workflow.meta_data.document_types || []), documentType];
  return workflow;
}

export function removeCategory(workflow, documentType) {
  const step = findDocumentProcessorStep(workflow.configuration);
  if (step) {
    step.config.document_extraction = step.config.document_extraction.filter((e) => e.document_type !== documentType);
  }
  if (workflow.meta_data?.document_types) {
    workflow.meta_data.document_types = workflow.meta_data.document_types.filter((t) => t !== documentType);
  }
  return workflow;
}

export function renameCategory(workflow, oldType, newType) {
  if (!newType || oldType === newType) return workflow;
  const extraction = findExtraction(workflow.configuration, oldType);
  if (extraction) extraction.document_type = newType;
  if (workflow.meta_data?.document_types) {
    workflow.meta_data.document_types = workflow.meta_data.document_types.map((t) => (t === oldType ? newType : t));
  }
  return workflow;
}
